import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateUserBalanceMutation } from '../../../../hooks/useDashboardQueries';
import { BalanceUpdateMode } from '../../../../types/dashboardV2';
import { formatUAHGrouped } from '../../../../shared/utils/currency';
import { parseApiError } from '../../../../utils/parseApiError';
import { DashboardModal } from './DashboardModal';
import { ChangeList } from './ConfirmChangeDialog';

interface BalanceModalProps {
  userId: string;
  currentBalance: number;
  onClose: () => void;
}

export const BalanceModal = memo(function BalanceModal({
  userId,
  currentBalance,
  onClose,
}: BalanceModalProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<BalanceUpdateMode>('adjust');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const mutation = useUpdateUserBalanceMutation(userId);

  const parsed = Number(amount);
  const isValid = amount.trim() !== '' && !Number.isNaN(parsed);

  const preview = useMemo(() => {
    if (!isValid) return currentBalance;
    return mode === 'set' ? parsed : currentBalance + parsed;
  }, [isValid, parsed, mode, currentBalance]);

  const handleSubmit = useCallback(() => {
    if (!isValid) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    mutation.mutate(
      { amount: parsed, mode, reason: reason.trim() || undefined },
      { onSuccess: onClose },
    );
  }, [isValid, confirming, parsed, mode, reason, mutation, onClose]);

  return (
    <DashboardModal
      titleId="balance-modal-title"
      icon="account_balance_wallet"
      title={t('dashboard.userDetails.editBalance')}
      subtitle={t('dashboard.userDetails.manualOverride')}
      onClose={onClose}
      error={
        mutation.isError
          ? parseApiError(mutation.error, t('dashboard.userDetails.balanceUpdateFailed')).message
          : null
      }
      cancelLabel={confirming ? t('dashboard.userDetails.confirm.back') : t('dashboard.userDetails.cancel')}
      onCancel={confirming ? () => setConfirming(false) : undefined}
      submitLabel={confirming ? t('dashboard.userDetails.confirm.confirm') : t('dashboard.userDetails.save')}
      submittingLabel={t('dashboard.userDetails.saving')}
      onSubmit={handleSubmit}
      isSubmitting={mutation.isPending}
      submitDisabled={!isValid}
    >
      {confirming ? (
        <div>
          <p className="ud-confirm-message">
            {t('dashboard.userDetails.confirm.balanceMessage', {
              from: formatUAHGrouped(currentBalance),
              to: formatUAHGrouped(preview),
            })}
          </p>
          <ChangeList
            changes={[
              {
                label: t('dashboard.userDetails.balance'),
                from: formatUAHGrouped(currentBalance),
                to: formatUAHGrouped(preview),
              },
            ]}
          />
          {reason.trim() && (
            <p className="ud-confirm-note">
              {t('dashboard.userDetails.confirm.reason', { reason: reason.trim() })}
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="balance-modal-current">
            <span>{t('dashboard.userDetails.currentBalance')}</span>
            <strong>{formatUAHGrouped(currentBalance)}</strong>
          </div>

          <div className="balance-mode-toggle" role="tablist" aria-label={t('dashboard.userDetails.modeAriaLabel')}>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'adjust'}
              className={`balance-mode-btn ${mode === 'adjust' ? 'active' : ''}`}
              onClick={() => setMode('adjust')}
            >
              {t('dashboard.userDetails.adjustBy')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'set'}
              className={`balance-mode-btn ${mode === 'set' ? 'active' : ''}`}
              onClick={() => setMode('set')}
            >
              {t('dashboard.userDetails.setTo')}
            </button>
          </div>

          <div>
            <label htmlFor="balance-amount" className="balance-field-label">
              {mode === 'adjust' ? t('dashboard.userDetails.deltaLabel') : t('dashboard.userDetails.newBalanceLabel')}
            </label>
            <div className="balance-amount-input-wrap">
              <input
                id="balance-amount"
                className="balance-amount-input"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(',', '.'))}
                placeholder={mode === 'adjust' ? t('dashboard.userDetails.deltaPlaceholder') : t('dashboard.userDetails.setPlaceholder')}
                aria-describedby="balance-preview"
              />
              <span className="balance-amount-input-suffix" aria-hidden="true">
                ₴
              </span>
            </div>
          </div>

          <div id="balance-preview" className="balance-preview">
            <span>{t('dashboard.userDetails.newBalanceLabel')}</span>
            <strong>{formatUAHGrouped(preview)}</strong>
          </div>

          <div>
            <label htmlFor="balance-reason" className="balance-field-label">
              {t('dashboard.userDetails.reasonOptional')}
            </label>
            <textarea
              id="balance-reason"
              className="balance-reason-input"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('dashboard.userDetails.reasonPlaceholder')}
              maxLength={500}
            />
          </div>
        </>
      )}
    </DashboardModal>
  );
});
